// src/map/plateOverlay/renderPlateBitmap.ts
//
// PDF.js rasterization for PlateOverlayLayer.
//
// Renders page 1 of a plate PDF to a WebP blob URL suitable for use as a
// MapLibre image source. Crops to the plan-view section. Memory-caps the
// canvas allocation for mobile devices.

import { loadPdfjs } from '../../lib/pdfjs';
import type { PlateRect } from './resolveCorners';

// ── PDF → bitmap ────────────────────────────────────────────────────────────

/**
 * Fetch, rasterize, and crop a plate PDF to a blob URL for MapLibre.
 *
 * Render-scale: pdfjs rasterizes the vector PDF to a canvas at
 * `scale * 72 DPI`. Bumped to scale=4 (2026-05-15) per audit; a typical
 * 8.5x11" plate at scale=4 is ~3300x4250 canvas.
 *
 * HI-5 audit 2026-05-22: on memory-constrained phones (iPhone with
 * deviceMemory < 6 GB, which covers iPhone 12 and older still in active
 * use), the 32 MP cap is too generous — reports of PlateOverlayLayer
 * crashing the tab. Drop to 10 MP on those. scale=4 typical plate is
 * ~14 MP so this *will* step down to ~scale 3.4 on low-memory phones,
 * producing a slightly less crisp but still legible bitmap.
 *
 * 2026-05-16 perf audit (F3): uses toBlob() + URL.createObjectURL()
 * instead of toDataURL() to avoid a 30-50 MB base64 string being retained
 * for the overlay's lifetime. Both canvases are zeroed immediately after
 * use so WebKit's GC doesn't pin the pixel buffers.
 */
export async function renderPlanViewBitmap(
  pdfUrl: string,
  planViewFraction: number,
  planViewRect: PlateRect | null,
  signal: AbortSignal,
  showFullPage: boolean = false,
): Promise<string> {
  const resp = await fetch(pdfUrl, { signal });
  if (!resp.ok) throw new Error(`PlateOverlay: PDF fetch ${resp.status}`);
  const buffer = await resp.arrayBuffer();
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

  const pdfjsLib = await loadPdfjs();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  try {
    const page = await pdf.getPage(1);

    // HI-5 audit 2026-05-22: on memory-constrained phones (deviceMemory < 6),
    // cap at 10 MP instead of 32 MP to prevent OOM crashes on iPhone 12 and
    // older. Preserved exactly from the original implementation.
    const nav = navigator as Navigator & { deviceMemory?: number };
    const lowMemoryDevice = typeof nav.deviceMemory === 'number' && nav.deviceMemory < 6;
    const MAX_PIXELS = lowMemoryDevice ? 10_000_000 : 32_000_000;
    let scale = 4;
    let viewport = page.getViewport({ scale });
    while (viewport.width * viewport.height > MAX_PIXELS && scale > 1) {
      scale -= 0.5;
      viewport = page.getViewport({ scale });
    }

    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('PlateOverlay: canvas 2d context unavailable');

    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

    // Crop selection:
    // - showFullPage=true: emit the entire page bitmap; pair with pageCorners.
    // - planViewRect provided (server georef): crop to that exact rectangle.
    // - Otherwise fall back to legacy 'top planViewFraction' crop.
    let cropX0: number, cropY0: number, cropW: number, cropH: number;
    if (showFullPage) {
      cropX0 = 0;
      cropY0 = 0;
      cropW = viewport.width;
      cropH = viewport.height;
    } else if (planViewRect) {
      cropX0 = Math.floor(viewport.width * planViewRect.minX);
      cropY0 = Math.floor(viewport.height * planViewRect.minY);
      cropW = Math.floor(viewport.width * (planViewRect.maxX - planViewRect.minX));
      cropH = Math.floor(viewport.height * (planViewRect.maxY - planViewRect.minY));
    } else {
      cropX0 = 0;
      cropY0 = 0;
      cropW = viewport.width;
      cropH = Math.floor(viewport.height * planViewFraction);
    }
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropW;
    cropCanvas.height = cropH;
    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) throw new Error('PlateOverlay: crop canvas context unavailable');
    cropCtx.drawImage(canvas, cropX0, cropY0, cropW, cropH, 0, 0, cropW, cropH);

    // Lossless WebP — lossy at 0.85 visibly smudges fine line work (DME
    // lozenges, FAF maltese cross, navaid hex outlines) at scale=4. Lossless
    // is ~2-3x the bytes but cost is purely local memory (never network).
    const blob = await new Promise<Blob>((resolve, reject) => {
      cropCanvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error('PlateOverlay: toBlob returned null')),
        'image/webp',
      );
    });
    // Zero both canvases to free their pixel buffers immediately. Without
    // this WebKit holds the 56-MB main + ~14-MB crop buffers until the
    // next major GC (which can be tens of seconds away).
    canvas.width = 0; canvas.height = 0;
    cropCanvas.width = 0; cropCanvas.height = 0;
    return URL.createObjectURL(blob);
  } finally {
    // HI-5 audit 2026-05-22: release pdfjs's internal buffers + worker
    // refs. Without this every plate render retains its parsed PDF
    // structure in memory until the next major GC, and on iPhone
    // Safari that's enough to OOM after 3–4 plates in a session.
    try { await pdf.destroy(); } catch { /* ignore */ }
  }
}
